import React, { useState, useEffect } from 'react';
import { QrCode, Download, Copy, Check } from 'lucide-react';
import QRCode from 'qrcode';
import { Button, Alert, LoadingSpinner } from './common';
import { SHORT_LINK_BASE_URL } from '../config';

const QRCodeGenerator = ({ shortCode, linkTitle = '', linkDescription = '' }) => {
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [format, setFormat] = useState('png');
  const [size, setSize] = useState(200);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (shortCode) {
      generateQRCode();
    }
  }, [shortCode, format, size]);

  const generateQRCode = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Generate the full URL for the QR code using short link base
      const fullUrl = `${SHORT_LINK_BASE_URL}/r/${shortCode}`;
      
      // QR code options
      const qrOptions = {
        width: size,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      };
      
      let qrCodeDataURL;
      if (format === 'svg') {
        qrCodeDataURL = await QRCode.toString(fullUrl, {
          ...qrOptions,
          type: 'svg'
        });
        // Convert SVG to data URL
        qrCodeDataURL = `data:image/svg+xml;base64,${btoa(qrCodeDataURL)}`;
      } else {
        qrCodeDataURL = await QRCode.toDataURL(fullUrl, qrOptions);
      }
      
      setQrCodeData(qrCodeDataURL);
    } catch (error) {
      console.error('Error generating QR code:', error);
      setError('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCode = async () => {
    try {
      if (!qrCodeData) {
        setError('No QR code generated');
        return;
      }
      
      // Create a download link
      const link = document.createElement('a');
      link.href = qrCodeData;
      link.download = `qr-${shortCode}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading QR code:', error);
      setError('Failed to download QR code');
    }
  };

  const copyQRCodeDataURL = async () => {
    try {
      await navigator.clipboard.writeText(qrCodeData);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying QR code:', error);
      setError('Failed to copy QR code');
    }
  };

  if (!shortCode) {
    return (
      <div className="text-center py-10 text-ink-500">
        <QrCode className="w-12 h-12 text-ink-400 mx-auto mb-4" />
        <p>No link selected for QR code generation</p>
      </div>
    );
  }

  const selectCls =
    'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500';

  return (
    <div className="space-y-6">
      {error && (
        <Alert type="error" message={error} onClose={() => setError(null)} />
      )}

      {/* Options + preview */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 space-y-3">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1.5">
              Format
            </label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className={selectCls}
            >
              <option value="png">PNG</option>
              <option value="svg">SVG</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1.5">
              Size
            </label>
            <select
              value={size}
              onChange={(e) => setSize(parseInt(e.target.value))}
              className={selectCls}
            >
              <option value={128}>128px</option>
              <option value={200}>200px</option>
              <option value={256}>256px</option>
              <option value={512}>512px</option>
            </select>
          </div>
          <Button
            onClick={generateQRCode}
            variant="secondary"
            disabled={loading}
            fullWidth
          >
            {loading ? 'Generating…' : 'Regenerate'}
          </Button>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-2xl border border-ink-200 bg-gradient-to-br from-ink-50 to-white p-6 flex items-center justify-center min-h-[280px]">
            {loading ? (
              <LoadingSpinner text="Generating QR code…" />
            ) : qrCodeData ? (
              <div className="text-center">
                <div className="bg-white p-4 rounded-2xl border border-ink-200 shadow-soft inline-block">
                  <img
                    src={qrCodeData}
                    alt={`QR Code for ${shortCode}`}
                    className="max-w-full h-auto"
                    style={{ width: `${size}px`, height: `${size}px` }}
                  />
                </div>
                <p className="mt-3 font-mono text-xs text-ink-500">
                  /r/{shortCode}
                </p>
              </div>
            ) : (
              <div className="text-center text-ink-400">
                <QrCode className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm">No QR code generated</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {qrCodeData && (
        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={downloadQRCode} variant="primary">
            <Download className="h-4 w-4" />
            Download {format.toUpperCase()}
          </Button>
          <Button onClick={copyQRCodeDataURL} variant="secondary">
            {copied ? (
              <Check className="h-4 w-4 text-emerald-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied ? 'Copied!' : 'Copy data URL'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default QRCodeGenerator;
